import type { ReactNode } from 'react';
import React, { createContext, useEffect, useRef } from 'react';
import { styled } from 'baseui';
import { useAtomValue } from 'jotai';
import { BrushCursorIcon } from '../../icons';
import { useStageAtoms } from '../../labelling-v2/components/stage-scope/hooks';
import { TOOL } from '../constants';
import useStage from './hooks/use-stage';
const Container = styled('div', {
  position: 'relative',
  height: '100%',
  width: '100%'
});
type CanvasContextT = {
  getCanvasContext: () => CanvasRenderingContext2D | undefined | null;
  getDrawingCanvasContext: () => CanvasRenderingContext2D | undefined | null;
};
export const CanvasContext = createContext<CanvasContextT>({
  getCanvasContext: () => null,
  getDrawingCanvasContext: () => null
});
type PropsT = {
  children: ReactNode;
};
export const CanvasProvider = ({
  children
}: PropsT) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const {
    brushToolSettingsAtom,
    selectedToolAtom
  } = useStageAtoms();
  const {
    size
  } = useAtomValue(brushToolSettingsAtom);
  const selectedTool = useAtomValue(selectedToolAtom);
  const {
    getStageDimension
  } = useStage();

  // https://html.spec.whatwg.org/multipage/canvas.html#concept-canvas-will-read-frequently
  const getDrawingCanvasContext = () => drawingCanvasRef.current?.getContext('2d', {
    willReadFrequently: true
  });
  const getCanvasContext = () => canvasRef.current?.getContext('2d', {
    willReadFrequently: true
  });
  useEffect(() => {
    const canvas = canvasRef.current;
    const drawingCanvas = drawingCanvasRef.current;
    const stageDimension = getStageDimension();
    if (stageDimension && canvas && drawingCanvas && (
    // even if same height width is set, new canvas is created.
    canvas.width !== stageDimension[0] || canvas.height !== stageDimension[1])) {
      canvas.width = stageDimension[0];
      canvas.height = stageDimension[1];
      drawingCanvas.width = stageDimension[0];
      drawingCanvas.height = stageDimension[1];
    }
  }, [getStageDimension]);
  const canvasStyles = {
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    cursor: selectedTool === TOOL.SEGMENTATION && size ? BrushCursorIcon(size) : 'inherit'
  };
  return <Container>
      {/* @ts-expect-error todo(ts-migration) TS2322 Type '{ height: string; width: string; position: string; top: number; left: number; cursor: string; }' is not assignable to type 'CSSProperties'. */}
      <canvas ref={canvasRef} style={canvasStyles} />
      <canvas ref={drawingCanvasRef}
    // @ts-expect-error todo(ts-migration) TS2322 Type '{ height: string; width: string; position: string; top: number; left: number; cursor: string; }' is not assignable to type 'CSSProperties'.
    style={{
      ...canvasStyles
    }} />
      <CanvasContext.Provider value={{
      getCanvasContext,
      getDrawingCanvasContext
    }}>
        {children}
      </CanvasContext.Provider>
    </Container>;
};